import React, { useMemo } from 'react'
import apiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyPageLayout,
  CompanyContentSections,
} from 'components/NewCompanyPage'
import { UnmatchedDoorsModal } from 'components/NewCompanyPage/Modals/UnmatchedDoorsModal'
import {
  MetricCard,
  StatsCard,
  Divider,
  DemographicsSection,
  DescriptionAndContactInfo,
} from 'components/NewCompanyPage/Sidebar/shared'
import { formatInteger, formatUsd, formatAddress } from 'utils/formatting'
import useDocumentTitle from 'components/useDocumentTitle'
import { useHideFooter } from 'context/layout'

export function NewHospitalPage({ hospitalID }: { hospitalID?: number }) {
  const api = apiService()
  const { data, isLoading } = api.useGetHospital(hospitalID)

  // Hide footer on company pages (must be called before loading check)
  useHideFooter()

  // Set document title
  useDocumentTitle(
    `${data?.hospital?.hospital_name || 'Not Found'} (Hospital) - First Bite`
  )

  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  const entityBreadcrumbs = useMemo(
    () =>
      data?.hospital?.hospital_name
        ? [{ title: 'Company', label: data.hospital.hospital_name }]
        : [],
    [data?.hospital?.hospital_name]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  // Build operational stats for the summary tab
  const operationalStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Provider Number',
      value: data?.hospital?.provider_number || 'N/A',
    },
    {
      label: 'Hospital Beds',
      value: data?.hospital?.total_hospital_beds
        ? formatInteger(data.hospital.total_hospital_beds)
        : 'N/A',
    },
    {
      label: 'Meals Per Day',
      value: data?.hospital?.meals_per_day
        ? formatInteger(data.hospital.meals_per_day)
        : 'N/A',
    },
    {
      label: 'Hospital Days',
      value: data?.hospital?.total_hospital_days
        ? formatInteger(data.hospital.total_hospital_days)
        : 'N/A',
    },
    {
      label: 'Data Year',
      value: data?.hospital?.year ? data.hospital.year.toString() : 'N/A',
    },
  ]

  // Financial stats focused on food service expenditures
  const financialStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Cafeteria Expenditures',
      value: data?.hospital?.cafeteria
        ? formatUsd(parseInt(data.hospital.cafeteria))
        : 'N/A',
    },
    {
      label: 'Patient Meal Expenditures',
      value: data?.hospital?.dietary
        ? formatUsd(parseInt(data.hospital.dietary))
        : 'N/A',
    },
    {
      label: 'Contracted Patient Meal Expenditures',
      value: data?.hospital?.dietary_under_contract
        ? formatUsd(parseInt(data.hospital.dietary_under_contract))
        : 'N/A',
    },
  ]

  // Build summary tab content
  const summaryTabContent: React.ReactNode[] = []

  // Description and Contact Info
  summaryTabContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      googlePlaceUrl={data?.hospital?.google_place_url || undefined}
      address={
        formatAddress({
          street: data?.hospital?.street_addr,
          city: data?.hospital?.city,
          state: data?.hospital?.state,
          zip: data?.hospital?.zip_code,
        }) ?? undefined
      }
    />
  )

  if (data?.doors_count && data?.doors_count > 0) {
    summaryTabContent.push(
      <MetricCard
        key="doors"
        value={data?.doors_count}
        label="Door Count"
        actionText="View Doors"
        onActionClick={() => setShowDoorsModal(true)}
      />
    )
  }

  // Operational Stats Section
  summaryTabContent.push(
    <StatsCard key="operational-stats" stats={operationalStats} />
  )
  summaryTabContent.push(<Divider key="divider-1" />)

  // Financial Stats Section
  summaryTabContent.push(
    <StatsCard key="financial-stats" stats={financialStats} />
  )

  // Build location details tab content
  const locationDetailsTabContent: React.ReactNode[] = []

  // Hospital Location - similar to other pages
  const hospitalLocation = data?.hospital?.street_addr
    ? `${data.hospital.city}, ${data.hospital.state} - USA`
    : 'N/A'

  locationDetailsTabContent.push(
    <MetricCard
      key="hospital-location"
      value={hospitalLocation}
      label="Hospital Location"
      variant="full-width"
    />
  )

  // Location Details Stats
  const locationInfoStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Full Address',
      value: data?.hospital?.street_addr || 'N/A',
    },
    {
      label: 'PO Box',
      value: data?.hospital?.po_box || 'N/A',
    },
    {
      label: 'ZIP Code',
      value: data?.hospital?.zip_code || 'N/A',
    },
    {
      label: 'State',
      value: data?.hospital?.state || 'N/A',
    },
    {
      label: 'City',
      value: data?.hospital?.city || 'N/A',
    },
    {
      label: 'Website',
      value: data?.hospital?.website_url || 'N/A',
    },
  ]

  locationDetailsTabContent.push(
    <StatsCard key="location-info" stats={locationInfoStats} />
  )

  if (data?.hospital?.address_info) {
    locationDetailsTabContent.push(
      <Divider key="divider-2" />,
      <DemographicsSection
        key="zipcode-demographics"
        addressInfo={data?.hospital?.address_info}
      />
    )
  }

  return (
    <>
      <CompanyPageLayout
        chainProxyId={data!.id!}
        breadcrumbRoot="Healthcare - Hospital"
        breadcrumbs={[
          {
            title: 'Hospital',
            breadcrumb: data!.hospital!.hospital_name!,
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        sidebar={{
          header: {
            name: data!.hospital!.hospital_name!,
            domain: data!.hospital!.domain || '',
            logoUrl: undefined, // Hospitals don't have logos in the data
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: summaryTabContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: locationDetailsTabContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={data!.id!}
          entityID={hospitalID}
          companyName={data!.hospital!.hospital_name!}
          websiteAddress={data!.hospital!.domain || ''}
          companyTypeSlug="healthcare-hospitals"
          tableTypeSlug="healthcare-hospitals"
        />
      </CompanyPageLayout>

      {/* Unmatched Doors Modal */}
      {data && (
        <UnmatchedDoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={data.id}
          chainData={{
            id: data.id,
            name: data.hospital!.hospital_name!,
            domain: data.hospital?.domain || undefined,
          }}
          subCategory={{
            name: 'Healthcare - Hospitals',
            slug: 'healthcare-hospitals',
            description: 'Healthcare - Hospitals',
          }}
          companyName={data.hospital?.hospital_name}
        />
      )}
    </>
  )
}
